import { useClaireDesigner } from '@/features/public-sites/claire-designer-flag';
import { CLAIRE_DESIGNER_FLAG } from '@borradh-workspace/contracts';
import { useRouterState } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { GlobalSearch } from '@/components/app/global-search';
import { LocationSwitcher } from '@/components/location-switcher';
import { NavChatHistory } from '@/components/nav-chat-history';
import { NavSecondary } from '@/components/nav-secondary';
import { NavSections } from '@/components/nav-sections';
import { NavUser } from '@/components/nav-user';
import { SidebarModeToggle } from '@/components/sidebar-mode-toggle';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization/get-active-organization.hook';
import { isProductionRuntime } from '@/lib/app-env';
import {
  type DashboardNavSection as NavSection,
  dashboardAssistantSection as assistantSection,
  filterNavForBookingDestination,
  filterNavForFeatureFlags,
  filterNavForProductionRuntime,
  isAdminOnlyBorradhNavigation,
  isDashboardSectionActive as isSectionActive,
  dashboardNavSections as navSections,
} from '@/lib/dashboard-nav';
import { useSession } from '@/lib/session';
import { useSidebarMode } from '@/lib/sidebar-mode';
import { useEnabledNavFlags } from '@/lib/use-enabled-nav-flags';

function getPosthogKey(): string | undefined {
  const config = typeof window !== 'undefined' ? window.__CONFIG__ : undefined;
  return (config as { posthogKey?: string } | undefined)?.posthogKey;
}

function AppSidebarResolved({
  claireAssistantEnabled,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  claireAssistantEnabled: boolean;
}) {
  const { enabled: designerEnabled } = useClaireDesigner();
  // Organizations that take bookings elsewhere have no appointments, till or
  // stock here, so the booking-system surface is hidden rather than shown
  // empty (ENG-500). An impersonating admin is the exception: they need these
  // surfaces to populate the internal diary before the booking switch flips.
  const { data: organization } = useActiveOrganization();
  const { data: session } = useSession();
  const isImpersonating = Boolean(session?.session?.impersonatedBy);
  const showAdminOnlyBadges = isAdminOnlyBorradhNavigation(
    organization?.bookingDestination,
    isImpersonating
  );
  const enabledFlags = useEnabledNavFlags();
  const sections = useMemo(() => {
    const withAssistant = claireAssistantEnabled
      ? [...navSections, assistantSection]
      : navSections;
    const navFlags = new Set(enabledFlags);
    if (designerEnabled) navFlags.add(CLAIRE_DESIGNER_FLAG);
    return filterNavForBookingDestination(
      filterNavForProductionRuntime(
        filterNavForFeatureFlags(withAssistant, navFlags),
        isProductionRuntime()
      ),
      organization?.bookingDestination,
      isImpersonating
    );
  }, [
    designerEnabled,
    claireAssistantEnabled,
    enabledFlags,
    organization?.bookingDestination,
    isImpersonating,
  ]);
  return (
    <AppSidebarInner
      {...props}
      // ENG-1161: the Browse/Chat toggle is shown to everyone. Claire is
      // already reachable without the `claire-assistant-sidebar` flag (home
      // prompt, "Ask Claire" buttons, `/assistant` itself); that flag only
      // governs the "Claire" nav entry. To gate the toggle on the same flag,
      // pass `claireAssistantEnabled` here instead.
      chatModeEnabled
      sections={sections}
      showAdminOnlyBadges={showAdminOnlyBadges}
    />
  );
}

function AppSidebarWithPostHogNav(props: React.ComponentProps<typeof Sidebar>) {
  const claireAssistantEnabled = useFeatureFlagEnabled(
    'claire-assistant-sidebar'
  );
  return (
    <AppSidebarResolved
      {...props}
      claireAssistantEnabled={Boolean(claireAssistantEnabled)}
    />
  );
}

/**
 * Single-column sidebar, taken from shadcn `sidebar-10`:
 *
 *   SidebarHeader   LocationSwitcher (the block's TeamSwitcher slot) + NavMain
 *   SidebarContent  NavSections (the block's NavWorkspaces) + NavSecondary
 *   SidebarFooter   NavUser (from sidebar-08)
 *   SidebarRail
 *
 * This replaced a two-tier `sidebar-09` rail — a 72px icon column plus a
 * secondary panel that appeared only for sections with sub-items. That shape
 * hid every label behind a tooltip and made the window change width as you
 * moved between sections. One column, always labelled, sections that expand in
 * place.
 */
function AppSidebarInner({
  sections,
  showAdminOnlyBadges,
  chatModeEnabled,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  sections: NavSection[];
  showAdminOnlyBadges: boolean;
  /** Show the Browse/Chat toggle (ENG-1161). Off = navigation only, as before. */
  chatModeEnabled: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { isMobile, setOpenMobile } = useSidebar();
  const [searchOpen, setSearchOpen] = useState(false);
  const sidebarMode = useSidebarMode(pathname);
  // With Chat mode off there is no toggle, so the stored mode must not strand
  // the user on a history panel they cannot leave.
  const mode = chatModeEnabled ? sidebarMode.mode : 'browse';
  const setMode = sidebarMode.setMode;

  // ⌘K / Ctrl-K. Lived in the desktop header until that was removed; the
  // shortcut is muscle memory, so it moved with the button rather than dying
  // with the header.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  // Settings is pinned to NavSecondary at the foot rather than listed with the
  // working sections — it is where you go to change the shop, not to run it.
  const workingSections = sections.filter((s) => s.slug !== 'settings');
  const settingsSection = sections.find((s) => s.slug === 'settings');

  return (
    <Sidebar className="border-r-0" {...props}>
      {/*
        `pb-0`: SidebarHeader's own `p-2` and the first nav group's `p-2` stack
        into a 16px gap under the switcher, against a 4px rhythm between the
        rows below it. Dropping the header's bottom padding leaves the group's
        8px — still a visible break between the branch and the nav, without the
        hole.
      */}
      <SidebarHeader className="pb-0">
        <LocationSwitcher />
        {chatModeEnabled ? (
          <SidebarModeToggle
            className="mt-1"
            mode={mode}
            onModeChange={setMode}
          />
        ) : null}
      </SidebarHeader>
      {/*
        `gap-0`: SidebarContent's own `gap-2` stacked with each group's `p-2` to
        put 24px between groups, three times the 8px seam under the switcher.
        The groups own their spacing (`pb-0` below), so every seam is 8px.
      */}
      <SidebarContent className="gap-0">
        {mode === 'chat' ? (
          // Its own scroll region, so a long history scrolls under the sticky
          // search row while Settings below stays pinned to the foot.
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <NavChatHistory onNavigate={handleNavigate} />
          </div>
        ) : (
          <NavSections
            onNavigate={handleNavigate}
            pathname={pathname}
            sections={workingSections}
            showAdminOnlyBadges={showAdminOnlyBadges}
          />
        )}
        <NavSecondary
          className="mt-auto pb-0"
          items={[
            ...(settingsSection
              ? [
                  {
                    title: settingsSection.title,
                    url: settingsSection.url,
                    icon: Settings,
                    isActive: isSectionActive(settingsSection, pathname),
                    onSelect: handleNavigate,
                  },
                ]
              : []),
          ]}
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
      <GlobalSearch onOpenChange={setSearchOpen} open={searchOpen} />
    </Sidebar>
  );
}

/**
 * PostHog hooks require `PostHogProvider`, which only mounts when
 * `VITE_PUBLIC_POSTHOG_KEY` is set (`posthog-provider.tsx`). Match
 * `dashboard/home.tsx`: without a key, skip flags and use default nav.
 */
export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  if (!getPosthogKey()) {
    return <AppSidebarResolved {...props} claireAssistantEnabled={false} />;
  }
  return <AppSidebarWithPostHogNav {...props} />;
}
